import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import "../../styles/layout/sidebar.scss";
import categoryIcon from "../../assets/images/categoryIcon.svg";
import { filteredNavBarLinkLists as initialNavBarLinkLists } from "../../utils/sideBarLinks";
import { fetchAllocateZoneById } from "services/allocateZone";
import { useQuery } from "@tanstack/react-query";
import { STALE_TIME } from "utils/constants";
import zoneLocalStorageId from "utils/zone";
import DynamicZoneLogo, {
  DEFAULT_APP_LOGO_SRC,
} from "components/Common/DynamicZoneLogo";
import {
  FaTachometerAlt,
  FaUserShield,
  FaStethoscope,
  FaUsers,
  FaFileInvoiceDollar,
  FaUserTie,
  FaUserMd,
  FaHospital,
  FaFlask,
  FaMicroscope,
  FaUserInjured,
  FaVials,
  FaCogs,
  FaBoxes,
  FaFileMedicalAlt,
  FaChartPie,
  FaCalculator,
  FaSyringe,
  FaFileSignature,
  FaUserPlus,
  FaThLarge,
} from "react-icons/fa";

const SECTION_ICONS = {
  Dashboard: <FaTachometerAlt />,
  "LIS Admin": <FaUserShield />,
  "Doctor OPD": <FaStethoscope />,
  Employees: <FaUsers />,
  "Accounts & Finance": <FaFileInvoiceDollar />,
  "Agents / Coordinators": <FaUserTie />,
  "Doctors Management": <FaUserMd />,
  "Franchise/Panel Management": <FaHospital />,
  "Lab Master Data": <FaFlask />,
  "Machine & Instrumentation": <FaMicroscope />,
  "Patient Management": <FaUserInjured />,
  "Sample & Accession": <FaVials />,
  Admin: <FaCogs />,
  Inventory: <FaBoxes />,
  Report: <FaFileMedicalAlt />,
  "MIS Report": <FaChartPie />,
  Estimate: <FaCalculator />,
  "Sample Collection": <FaSyringe />,
  "Result Entry": <FaFileSignature />,
  "New Registration": <FaUserPlus />,
};

const getSectionIcon = (sectionName) => {
  return SECTION_ICONS[sectionName] || <FaThLarge />;
};


function SideBar({ isSidebarExpanded, toggleSidebar }) {
  const { pathname } = useLocation();
  const login = useSelector((state) => state.jwtAuthreducer);
  const [isMobile, setIsMobile] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [allowedSections, setAllowedSections] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isFranchiseEmployee] = useState(() =>
    Number(localStorage.getItem("is_franchise_employee")),
  );
  const [isSuperFranchise] = useState(() =>
    Number(localStorage.getItem("is_super_franchise")),
  );
  const [isSubFranchise] = useState(() =>
    Number(
      localStorage.getItem("is_sub_franchise") ??
        localStorage.getItem("sub_franchise"),
    ),
  );
  const hiddenCategories = ["Employees"];
  const [openCategory, setOpenCategory] = useState(null);

  const auth = useSelector((state) => state.jwtAuthreducer);

  const {
    data: zoneData,
    isLoading: isLoadingZone,
    isError,
    error,
    refetch,
  } = useQuery(
    ["fetch-zone-by-id", auth?.user?.employee_code_id],
    () =>
      fetchAllocateZoneById({ employee_code_id: auth?.user?.employee_code_id }),
    {
      staleTime: STALE_TIME,
      enabled: !!auth?.user?.employee_code_id,
    },
  );

  const zoneId = zoneLocalStorageId();

  const matchedZone = zoneData?.data?.find((item) => item.zone_id === zoneId);

  const isB2C = matchedZone?.zone_obj?.zone_type_b2c;

  const linkAccessRoles = {
    "/panne-wise-bus-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
        "FRANCHISE_EMPLOYEE",
        "SUB_FRANCHISE",
        "SUPER_FRANCHISE",
      ],
      allowCombined: true,
    },
    "/patient-appointment-details": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
        "FRANCHISE_EMPLOYEE",
        "SUB_FRANCHISE",
        "SUPER_FRANCHISE",
      ],
      allowCombined: true,
    },
    "/appointment-payment": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
        "FRANCHISE_EMPLOYEE",
        "SUB_FRANCHISE",
        "SUPER_FRANCHISE",
      ],
      allowCombined: true,
    },
    "/patient": {
      roles: [
        "FRANCHISE_EMPLOYEE",
        "SUB_FRANCHISE",
        "CEO",
        "SUPER_FRANCHISE",
        "SUPER_ADMIN",
      ],
      allowCombined: true,
    },
    "/sample-collect-by-barcode": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/online-payment-details": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
        "FRANCHISE_EMPLOYEE",
        "SUB_FRANCHISE",
        "SUPER_FRANCHISE",
      ],
      allowCombined: true,
    },
    "/patient-report": {
      roles: [
        "SUB_FRANCHISE",
        "SUPER_FRANCHISE",
        "CEO",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
        "FRANCHISE_EMPLOYEE",
      ],
      allowCombined: true,
    },
    "/franchise-advance-payment-list": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FRANCHISE_EMPLOYEE",
        "SUB_FRANCHISE",
        "SUPER_FRANCHISE",
      ],
      allowCombined: true,
    },
    "/franchise-investigation-details": {
      roles: ["FRANCHISE_EMPLOYEE", "CEO", "SUB_FRANCHISE", "SUPER_FRANCHISE"],
      superFranchiseOnly: true,
    },
    "/advance-payment": {
      roles: ["SUB_FRANCHISE", "FRANCHISE_EMPLOYEE", "SUPER_FRANCHISE", "CEO"],
      allowCombined: true,
    },
    "/franchise-panel-wise-bus-report": {
      roles: [
        "FRANCHISE_EMPLOYEE",
        "ACCOUNTANT",
        "CEO",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
        "SUPER_FRANCHISE",
      ],
      excludeCombined: true,
      superFranchiseOnly: true,
    },
    "/franchise": {
      roles: ["SUPER_FRANCHISE", "ACCOUNTANT", "CEO", "FRANCHISE_EMPLOYEE"],
      excludeCombined: true,
      superFranchiseOnly: true,
    },
    "/test-rate-list-excel": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "FRANCHISE_EMPLOYEE",
        "SUPER_FRANCHISE",
      ],
      excludeCombined: true,
      superFranchiseOnly: true,
    },
    "/panel-master": {
      roles: ["FRANCHISE_EMPLOYEE", "CEO", "SUPER_FRANCHISE"],
      excludeCombined: true,
      superFranchiseOnly: true,
    },
    "/commission-report": {
      roles: [
        "SUPER_FRANCHISE",
        "FRANCHISE_EMPLOYEE",
        "CEO",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
      excludeCombined: true,
      superFranchiseOnly: true,
    },
    "/credit-transfer": {
      roles: ["SUPER_FRANCHISE", "CEO", "FRANCHISE_EMPLOYEE"],
      excludeCombined: true,
      superFranchiseOnly: true,
    },
    "/package-copy-rate": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FRANCHISE_EMPLOYEE",
        "SUPER_FRANCHISE",
      ],
      excludeCombined: true,
      superFranchiseOnly: true,
    },
    "/commission-report-ledger": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "FRANCHISE_EMPLOYEE",
        "SUPER_FRANCHISE",
      ],
      excludeCombined: true,
      superFranchiseOnly: true,
    },
    "/franchise-copy-rates": {
      roles: ["FRANCHISE_EMPLOYEE", "ACCOUNTANT", "CEO", "SUPER_FRANCHISE"],
      excludeCombined: true,
    },
    "/estimate": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
        "SUPER_FRANCHISE",
      ],
    },
    "/investigation-deliveryDays": {
      roles: ["SUPER_ADMIN", "CEO", "SUPER_FRANCHISE"],
    },
    "/branch": { roles: ["SUPER_ADMIN", "CEO"] },
    "/department": { roles: ["SUPER_ADMIN", "CEO"] },
    "/designation": { roles: ["SUPER_ADMIN", "CEO"] },
    "/employee": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
      ],
    },
    "/employeeType": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/employeeCategory": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/leave-master": { roles: ["SUPER_ADMIN", "SUPER ADMIN", "CEO"] },
    "/employeeLeave": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
      ],
    },
    "/leaveLedgerSummary": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/attendance-report": { roles: ["SUPER_ADMIN", "CEO"] },
    "/payments": { roles: ["SUPER_ADMIN", "CEO"] },
    "/doctor-ip-calculation": { roles: ["SUPER_ADMIN", "CEO"] },
    "/lab-department-wise-ip-rates": { roles: ["SUPER_ADMIN", "CEO"] },
    "/rejected-sample-appointments": { roles: ["SUPER_ADMIN", "CEO"] },
    "/online-payments-and-receive-payment": { roles: ["SUPER_ADMIN", "CEO"] },
    "/machine-data": { roles: ["SUPER_ADMIN", "CEO"] },
    "/kyc-documents": { roles: ["SUPER_ADMIN", "CEO"] },
    "/new-franchise": { roles: ["SUPER_ADMIN", "CEO"] },
    "/zone": { roles: ["SUPER_ADMIN", "CEO"] },
    "/area": { roles: ["SUPER_ADMIN", "CEO"] },
    "/circular": { roles: ["SUPER_ADMIN", "CEO"] },
    "/collection-type": { roles: ["SUPER_ADMIN", "CEO"] },
    "/paymentMode": { roles: ["SUPER_ADMIN", "CEO"] },
    "/lab-department": { roles: ["SUPER_ADMIN", "CEO"] },
    "/department-type": { roles: ["SUPER_ADMIN", "CEO"] },
    "/franchise-type": { roles: ["SUPER_ADMIN", "CEO"] },
    "/payment-ledger": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/commission-ledger": { roles: ["SUPER_ADMIN", "CEO"] },
    "/discount-reason": { roles: ["SUPER_ADMIN", "CEO"] },
    "/doctors-degree": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/doctors-sepcialization": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/organism": { roles: ["SUPER_ADMIN", "CEO"] },
    "/roles": { roles: ["SUPER_ADMIN", "CEO"] },
    "/document": { roles: ["SUPER_ADMIN", "CEO"] },
    "/shareType": { roles: ["SUPER_ADMIN", "CEO"] },
    "/sampleType": { roles: ["SUPER_ADMIN", "CEO"] },
    "/sampleRejectionReason": { roles: ["SUPER_ADMIN", "CEO"] },
    "/payment-type": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-test": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-template": { roles: ["SUPER_ADMIN", "CEO"] },
    "/emp-discount-allowed": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-observation": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-sequence": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-nabl": { roles: ["SUPER_ADMIN", "CEO"] },
    "/outsource-investigation": { roles: ["SUPER_ADMIN", "CEO"] },
    "/outsource-lab": { roles: ["SUPER_ADMIN", "CEO"] },
    "/package": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/test-rate": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
      ],
    },
    "/panel-master-approval": {
      roles: ["FRANCHISE_EMPLOYEE", "CEO"],
      excludeCombined: true,
    },
    "/investigation-help": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-interpretation": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-comment": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-details": { roles: ["SUPER_ADMIN", "CEO"] },
    "/doctors": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/employee-franchise-details": {
      roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"],
    },
    "/observation": { roles: ["SUPER_ADMIN", "CEO"] },
    "/investigation-manager": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/sample-collection": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "REPORT_TYPIST",
        "REPORT TYPIST",
        "LABORATORY_TECHNICIAN",
        "LABORATORY TECHNICIAN",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
      ],
    },
    "/performing-doctor-charges": {
      roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"],
    },
    "/machine-master": { roles: ["SUPER_ADMIN", "CEO"] },
    "/observation-host-code": { roles: ["SUPER_ADMIN", "CEO"] },
    "/report-role": { roles: ["SUPER_ADMIN", "CEO"] },
    "/assign-role": { roles: ["SUPER_ADMIN", "CEO"] },
    "/received-payment": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/product": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/product-type": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/vendor": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/terms-condition": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/purchase-order": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/stock-in": { roles: ["SUPER_ADMIN", "CEO", "ACCOUNTANT"] },
    "/medicity-panel-wise-bus-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/patient-wise-search-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "REPORT_TYPIST",
        "REPORT TYPIST",
        "LABORATORY_TECHNICIAN",
        "LABORATORY TECHNICIAN",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/payment-type-search-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "RECEPTIONIST",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
      ],
    },
    "/franchise-top-performer": { roles: ["SUPER_ADMIN", "CEO"] },
    "/registration": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/lab-department-wise-coordinator-rates": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/patient-coordinator-commission": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/patient-coordinator-agent-onboarding-application": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/doctor-onboarding-application": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/franchise-onboarding-application": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/membership-card": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FLOOR MANAGER",
        "FLOOR_MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/membership-rates": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/membership-orders": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FLOOR MANAGER",
        "FLOOR_MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/patient-coordinator-agents": {
      roles: ["SUPER_ADMIN", "CEO", "SUPER ADMIN", "ADMIN"],
    },
    "/patient-coordinator-agent-onboarding-application-approval": {
      roles: ["SUPER_ADMIN", "CEO", "SUPER ADMIN", "ADMIN"],
    },
    "/doctor-onboarding-application-approval": {
      roles: ["SUPER_ADMIN", "CEO", "SUPER ADMIN", "ADMIN"],
    },
    "/franchise-onboarding-application-approval": {
      roles: ["SUPER_ADMIN", "CEO", "SUPER ADMIN", "ADMIN"],
    },
    "/navbar-section": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/invoice-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR_MANAGER",
        "FLOOR MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/franchise-invoice-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FRANCHISE_EMPLOYEE",
        "SUB_FRANCHISE",
        "SUPER_FRANCHISE",
      ],
      allowCombined: true,
    },
    "/collection-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR MANAGER",
        "FLOOR_MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/booking-collection-report": {
      roles: ["SUPER_ADMIN", "CEO", "SUPER ADMIN", "ADMIN"],
    },
    "/card-collection-report": {
      roles: ["SUPER_ADMIN", "CEO", "SUPER ADMIN", "ADMIN"],
    },
    "/investigation-wise-report": {
      roles: ["SUPER_ADMIN", "CEO", "SUPER ADMIN", "ADMIN"],
    },
    "/all-payment-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR MANAGER",
        "FLOOR_MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/discount-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR MANAGER",
        "FLOOR_MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/outstanding-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "ACCOUNTANT",
        "FLOOR MANAGER",
        "FLOOR_MANAGER",
        "RECEPTIONIST",
      ],
      allowCombined: true,
    },
    "/lab-department-ip-rate": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/package-investigation": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/investigationpackage": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/observation-entry": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/role-wise-patient-report": {
      roles: [
        "SUPER_ADMIN",
        "CEO",
        "FLOOR MANAGER",
        "FLOOR_MANAGER",
        "RECEPTIONIST",
      ],
    },
    "/franchiseFormat": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/print-label-exe": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/view-purchase-order": {
      roles: ["SUPER_ADMIN", "ACCOUNTANT", "CEO"],
    },
    "/section": {
      roles: ["SUPER_ADMIN", "CEO"],
    },
    "/tat-report": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/tat-graphical-report": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/appointment-cancellations": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/marketing-person-business-report": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/employee-targets": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/doctor-clinic-router": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/doctor-opd-charge-router": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/doctor-schedule-router": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/doctor-opd-bill-item-router": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/doctor-opd-bill-payment-router": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
    "/doctor-opd-appointment-router": {
      roles: ["CEO", "SUPER_ADMIN", "SUPER ADMIN", "ADMIN"],
      allowCombined: true,
    },
  };

  useEffect(() => {
    const roles = [];

    if (isSuperFranchise === 1) roles.push("SUPER_FRANCHISE");
    if (isFranchiseEmployee === 1) roles.push("FRANCHISE_EMPLOYEE");
    if (isSubFranchise === 1) roles.push("SUB_FRANCHISE");

    const employeeRoles = login?.user?.employee_roles || [];
    let superAdminExists = false;

    employeeRoles.forEach((role) => {
      const roleName = role?.role_name;
      if (roleName && !roles.includes(roleName)) {
        roles.push(roleName);
      }

      if (roleName === "SUPER_ADMIN" || roleName === "SUPER ADMIN") {
        superAdminExists = true;
      }
    });

    setUserRoles(roles);
    setIsSuperAdmin(superAdminExists);

    if (!superAdminExists) {
      const sections = login?.user?.allowed_section || [];
      setAllowedSections(sections);
    } else {
      setAllowedSections([]);
    }
  }, [login, isSuperFranchise, isFranchiseEmployee, isSubFranchise]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const employee_code_id = login?.user?.employee_code_id || null;
  const hasFullAccess = employee_code_id === 1;
  const hasAnyAccess = () => {
    if (hasFullAccess) return true;
    if (isSuperAdmin) return true;
    if (allowedSections && allowedSections.length > 0) return true;
    if (userRoles && userRoles.length > 0) return true;
    return false;
  };

  const normalizeSectionName = (name) => {
    return name?.toLowerCase().replace(/[\s-_]/g, "") || "";
  };

  const isSectionAllowed = (sectionName) => {
    if (hasFullAccess) return true;
    if (isSuperAdmin) return true;

    if (!hasAnyAccess()) return false;

    if (allowedSections && allowedSections.length > 0) {
      const normalizedSectionName = normalizeSectionName(sectionName);
      return allowedSections.some((allowedSection) => {
        const normalizedAllowedSection = normalizeSectionName(
          allowedSection.section,
        );
        return normalizedAllowedSection === normalizedSectionName;
      });
    }

    return true;
  };

  const isLinkAllowedBySection = (linkName, categoryName) => {
    if (hasFullAccess) return true;
    if (isSuperAdmin) return true;

    if (!hasAnyAccess()) return false;

    if (allowedSections && allowedSections.length > 0) {
      return isSectionAllowed(linkName);
    }

    return true;
  };

  const hasAccess = (link, linkName = "", categoryName = "") => {
    if (hasFullAccess) return true;
    if (isSuperAdmin) return true;

    if (!hasAnyAccess()) return false;

    const accessConfig = linkAccessRoles[link];

    if (!accessConfig) {
      return allowedSections && allowedSections.length > 0
        ? isLinkAllowedBySection(linkName, categoryName)
        : userRoles.length > 0;
    }

    const allowedRoles =
      typeof accessConfig === "object" && accessConfig.roles
        ? accessConfig.roles
        : accessConfig;

    if (userRoles.includes("SUPER_ADMIN") || userRoles.includes("SUPER ADMIN"))
      return true;

    const isSuperFranchiseAndFranchiseEmployee =
      isFranchiseEmployee === 1 && isSuperFranchise === 1;
    const isFranchiseEmployeeAndSubFranchise =
      isFranchiseEmployee === 1 && isSubFranchise === 1;

    const hasRoleAccess = () => {
      if (isSuperFranchiseAndFranchiseEmployee) {
        if (accessConfig.superFranchiseOnly) {
          return allowedRoles.includes("SUPER_FRANCHISE");
        }

        if (accessConfig.excludeCombined) {
          return false;
        }

        if (accessConfig.allowCombined) {
          return allowedRoles.some((role) => userRoles.includes(role));
        }
      }

      if (isFranchiseEmployeeAndSubFranchise) {
        if (accessConfig.excludeCombined) {
          return false;
        }

        if (accessConfig.allowCombined) {
          return allowedRoles.some((role) => userRoles.includes(role));
        }
      }

      return allowedRoles.some((role) => userRoles.includes(role));
    };

    if (allowedSections && allowedSections.length > 0) {
      const hasSectionAccess = isLinkAllowedBySection(linkName, categoryName);
      const isFranchiseRole =
        isFranchiseEmployee === 1 ||
        isSuperFranchise === 1 ||
        isSubFranchise === 1;
      return isFranchiseRole
        ? hasSectionAccess && hasRoleAccess()
        : hasSectionAccess;
    }

    return hasRoleAccess();
  };

  const normalizePath = (path) => {
    if (!path || path === "/") return "/";
    return path.replace(/\/+$/, "");
  };

  const isPathActive = (link) => {
    const currentPath = normalizePath(pathname);
    const targetPath = normalizePath(link);

    if (targetPath === "/") return currentPath === "/";
    return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
  };

  const filteredNavBarLinkLists = initialNavBarLinkLists
    .map((category) => {
      const filteredLinks = category.links
        .filter((link) => hasAccess(link.link, link.name, category.category))
        .sort((a, b) => a.name.localeCompare(b.name));

      return {
        ...category,
        links: filteredLinks,
      };
    })
    .filter((category) => {
      if (!category || category.links.length === 0) return false;

      if (category.category === "Doctor OPD" && !isB2C) {
        return false;
      }

      const isFranchiseRole =
        isFranchiseEmployee === 1 ||
        isSuperFranchise === 1 ||
        isSubFranchise === 1;
      const hasEmployeeLink = category.links.some((link) =>
        link.link.includes("/employee"),
      );

      if (
        isFranchiseRole &&
        hiddenCategories.includes(category.category) &&
        !hasEmployeeLink
      ) {
        return false;
      }

      return true;
    });

  // Auto-expand the dropdown that contains the current active route, and close any other dropdowns
  useEffect(() => {
    const activeCategory = filteredNavBarLinkLists.find((category) =>
      category.links.some((link) => isPathActive(link.link)),
    );
    if (activeCategory) {
      setOpenCategory(activeCategory.category);
    } else {
      setOpenCategory(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, filteredNavBarLinkLists.length]);

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      if (isSidebarExpanded) {
        toggleSidebar();
      }
    }
  };

  const handleDropdownToggle = (categoryName) => {
    setOpenCategory((prev) => (prev === categoryName ? null : categoryName));
  };

  return (
    <div
      className={` ${!isSidebarExpanded
        ? "sidebar"
        : window.innerWidth <= 768
          ? "sidebar"
          : "sidebarClose"
        } ${isSidebarExpanded ? "sidebarMobileExpand" : ""}`}
    >
      <div className="logo d-none d-md-flex justify-content-center align-items-center">
        <a href="/" aria-label="Home">
          <DynamicZoneLogo
            fallbackSrc={DEFAULT_APP_LOGO_SRC}
            alt="Logo"
            title="Logo"
            className="sidebar-zone-logo"
          />
        </a>
      </div>
      <div className="menu">
        <h3>General</h3>
        <ul>
          {isSectionAllowed("Dashboard") && (
            <li className="acc">
              <Link
                to="/"
                className={`collapsed ${isPathActive("/") ? "active" : ""}`}
                onClick={handleLinkClick}
              >
                {getSectionIcon("Dashboard")}
                <span>Dashboard</span>
              </Link>
            </li>
          )}

          {employee_code_id === 1 && (
            <li className="acc">
              <Link
                to="/create"
                className={`collapsed ${isPathActive("/create") ? "active" : ""}`}
                onClick={handleLinkClick}
              >
                {getSectionIcon("LIS Admin")}
                <span>LIS Admin</span>
              </Link>
              <div className="collapse" id="collapseExample1">
                <div className="acc-links"></div>
              </div>
            </li>
          )}
          {filteredNavBarLinkLists.map((category, index) => {
            const hasActiveChild = category.links.some((link) =>
              isPathActive(link.link),
            );
            const isOpen = openCategory === category.category;

            return (
              <li
                className={`acc ${hasActiveChild ? "has-active-child" : ""}`}
                key={index}
              >
                <a
                  href="#dropdown"
                  className={`${!isOpen ? "collapsed" : ""} ${
                    hasActiveChild ? "active" : ""
                  }`}
                  role="button"
                  aria-expanded={isOpen ? "true" : "false"}
                  aria-controls={`collapseExample${index}`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDropdownToggle(category.category);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {getSectionIcon(category.category)}
                  <span>{category.category}</span>
                  <span className="angle angleDown ms-auto"></span>
                </a>
                <div
                  className={`collapse ${isOpen ? "show" : ""}`}
                  id={`collapseExample${index}`}
                >
                  <div className="acc-links">
                    {category.links.map((link, idx) => {
                      const isActive = isPathActive(link.link);
                      return (
                        <Link
                          onClick={handleLinkClick}
                          style={{ marginBottom: "10px" }}
                          to={link.link}
                          className={`acc-link ${isActive ? "active" : ""}`}
                          key={idx}
                        >
                          <span className="sidebar-icon">{link.icon}</span>
                          {link.name}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </li>
            );
          })}

          {/* Estimate Link */}
          {hasAccess("/estimate", "Estimate", "") &&
            isSectionAllowed("Estimate") && (
              <li>
                <Link
                  onClick={handleLinkClick}
                  to="/estimate"
                  className={`acc-link ${isPathActive("/estimate") ? "active" : ""
                    }`}
                >
                  {getSectionIcon("Estimate")}
                  <span>Estimate</span>
                </Link>
              </li>
            )}

          {/* Sample Collection Link */}
          {hasAccess("/sample-collection", "Sample Collection", "") &&
            isSectionAllowed("Sample Collection") && (
              <li>
                <Link
                  onClick={handleLinkClick}
                  to="/sample-collection"
                  className={`acc-link ${isPathActive("/sample-collection") ? "active" : ""
                    }`}
                >
                  {getSectionIcon("Sample Collection")}
                  <span>Sample Collection</span>
                </Link>
              </li>
            )}

          {/* Result Entry Link */}
          {hasAccess("/patient-wise-search-report", "Result Entry", "") &&
            isSectionAllowed("Result Entry") && (
              <li>
                <Link
                  onClick={handleLinkClick}
                  to="/patient-wise-search-report"
                  className={`acc-link ${isPathActive("/patient-wise-search-report") ? "active" : ""
                    }`}
                >
                  {getSectionIcon("Result Entry")}
                  <span>Result Entry</span>
                </Link>
              </li>
            )}

          {/* New Registration Link */}
          {isSectionAllowed("New Registration") && (
            <li>
              <Link
                onClick={handleLinkClick}
                to="/registration"
                className={`acc-link ${isPathActive("/registration") ? "active" : ""
                  }`}
              >
                {getSectionIcon("New Registration")}
                <span>New Registration</span>
              </Link>
            </li>
          )}
        </ul>
      </div>
      <div className="sidebar-brand-footer" aria-label="Labh Path Labs">
        <img src={DEFAULT_APP_LOGO_SRC} alt="Labh Path Labs" title="Labh Path Labs" />
      </div>
    </div>
  );
}

export default SideBar;
